import React, { FC, useCallback, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import SelectionBulkMenu from '../../../shared/components/SelectionBulkMenu';
import AnalyseDropDown from '../../../shop/analyse/AnalyseDropDown';
import AnalyseTableCollection from '../../../shop/analyse/AnalyseTableCollection';
import SelectionBulkContainer from '../../../shop/selectionlists/header/SelectionBulkContainer';
import {
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    CompleteCollectionAnalyseResponseOut
} from '../../../store/api';
import { useTranslation } from 'react-i18next';
import { fetchCollectionAnalyse } from './api/apiFetchAnalyse';
import { SelectionList } from '../../../../models/SelectionList';
import { SubscriptionPeriodType } from '../../../../models/Article';
import { PriceTier } from '../../../../models/PriceTier';
import { ProductSelection } from '../../../shared/product-selection/ProductSelectionTypes';
import { getProductSelectionCount } from '../../../shared/product-selection/ProductSelectionUtils';
import { useStickyHeader } from '../../../shared/header/useStickyHeader';
import { COLLECTION_NAVIGATION_V2, useFeature } from '../../../shared/utils/togglz';

const StBreadcrumbs = styled.ul`
    display: flex;
    font-size: 16px;
`;

const StHeader = styled.div`
    display: flex;
`;

interface Props {
    cancelSelectionMode: () => void;
    containsEpressProducts: boolean;
    isInSelectionMode: boolean;
    isNothingSelected: boolean;
    onBulkAddToCart: (
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    onBulkAddToGroup: (productGroupId: number) => void;
    onBulkAddToList: (
        list: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    productSelection: ProductSelection;
}

export const CompleteCollectionHeader: FC<Props> = ({
    cancelSelectionMode,
    containsEpressProducts,
    isInSelectionMode,
    isNothingSelected,
    onBulkAddToCart,
    onBulkAddToGroup,
    onBulkAddToList,
    productSelection
}) => {
    const { t } = useTranslation();
    const { isSticky } = useStickyHeader();
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);

    const [analyse, setAnalyse] = useState<CompleteCollectionAnalyseResponseOut | undefined>(undefined);

    const [isAnalyseOpen, setIsAnalyseOpen] = useState(false);
    const [isAnalyseLoading, setIsAnalyseLoading] = useState(true);
    const onAnalyseClicked = useCallback(() => setIsAnalyseOpen(prevState => !prevState), []);

    useLayoutEffect(() => {
        const fetchAnalyse = async () => {
            if (isAnalyseOpen) {
                try {
                    setIsAnalyseLoading(true);
                    const res = await fetchCollectionAnalyse();
                    setAnalyse(res);
                } catch (e) {
                    setAnalyse(undefined);
                } finally {
                    setIsAnalyseLoading(false);
                }
            }
        };

        fetchAnalyse().then();
    }, [isAnalyseOpen]);

    const bulkMenu = (
        <SelectionBulkMenu
            isInSelectionMode={isInSelectionMode}
            cancelSelection={cancelSelectionMode}
            selectedCount={getProductSelectionCount(productSelection)}
            onBulkAddToCart={onBulkAddToCart}
            onBulkAddToList={onBulkAddToList}
            onBulkAddToGroup={onBulkAddToGroup}
            deleteLabel={t('deleteFromList')}
            isAddToCartEnabled={true}
            isAddToListEnabled={true}
            isAddToProductGroupEnabled={true}
            showSubscriptionStartDateOption={containsEpressProducts}
            isNothingSelected={isNothingSelected}
        />
    );

    const header = (
        <StHeader className="selection-detail-list">
            {isCollectionNavigationV2Enabled ? <CollectionV2Breadcrumbs /> : <CollectionBreadcrumbs />}
            <AnalyseDropDown
                analysis={analyse}
                tableComponent={AnalyseTableCollection}
                onAnalyseClicked={onAnalyseClicked}
                title="Analyse collection"
                label="Analyse"
                className="collection-analyse"
                showDropdown={isAnalyseOpen}
                isLoading={isAnalyseLoading}
            />
        </StHeader>
    );

    return (
        <SelectionBulkContainer
            movedUp={isInSelectionMode}
            isSticky={isSticky}
            headerComponent={header}
            bulkMenuComponent={bulkMenu}
            headerHeight={64}
        />
    );
};

const CollectionBreadcrumbs = () => {
    const { t } = useTranslation();
    return (
        <StBreadcrumbs>
            <li>
                <i className="bolinda-icons" style={{ fontSize: 'inherit', marginRight: '0.3em' }}>
                    your_collection
                </i>
            </li>
            <li>{t('menu.yourBorrowbox.yourCollectionMain')}</li>
            <li className="material-icons md-big">keyboard_arrow_right</li>
            <li>{t('menu.yourBorrowbox.yourCollection.completeCollection')}</li>
        </StBreadcrumbs>
    );
};

const CollectionV2Breadcrumbs = () => {
    const { t } = useTranslation();
    return (
        <StBreadcrumbs>
            <li>{t('menu.yourBorrowbox.yourCollectionMain')}</li>
        </StBreadcrumbs>
    );
};
